// src/lib/api.js
// Minimal client for /api/traffic/* endpoints + helpers used by the Stats page.

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function firstOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function nextMonth(d)   { return new Date(d.getFullYear(), d.getMonth() + 1, 1); }
function addDays(d, n)  { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

// ------------------------------ plate normalization ------------------------------
/**
 * Prod-style rule:
 *  - Default: PARTIAL match
 *  - If input starts with "=" => EXACT match (value after "=")
 */
function normalizePlate(raw) {
  const s = String(raw || "").trim();
  if (!s) return { value: "", mode: "none" };          // no plate param
  if (s.startsWith("=")) return { value: s.slice(1).trim(), mode: "exact" };
  return { value: s, mode: "partial" };
}

// ------------------------------ bucket/light helpers ------------------------------
const VALID_BUCKETS = new Set(["overnight", "morning", "afternoon", "evening"]);

// Map legacy/loose inputs to canonical buckets. Supports comma lists.
function canonicalizeBuckets(bucketStr) {
  if (!bucketStr) return "";
  const parts = String(bucketStr)
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .map((b) => (b === "night" ? "overnight" : b)) // legacy compat
    .filter((b) => VALID_BUCKETS.has(b));
  // de-dup & join
  return Array.from(new Set(parts)).join(",");
}

// Apply shared filters to a URLSearchParams
function applyFilters(qs, { bucket = "", light = "", plate = "" } = {}) {
  // Mutually exclusive: if light is set, prefer it over bucket
  const l = String(light || "").toLowerCase();
  if (l === "night") qs.set("is_night", "1");
  else if (l === "day") qs.set("is_night", "0");
  else {
    const b = canonicalizeBuckets(bucket);
    if (b) qs.set("buckets", b);
  }

  if (plate) qs.set("plate", plate);
}

// ---------- URL builders (end is EXCLUSIVE where applicable) ----------
export function urlHourly({ day, bucket = "", light = "", plate = "" }) {
  const start = ymd(day);
  const end = ymd(addDays(day, 1));
  const qs = new URLSearchParams({ period: "custom", start, end });
  applyFilters(qs, { bucket, light, plate });
  return `/api/traffic/hourly?${qs.toString()}`;
}
export function urlDaily({ month, bucket = "", light = "", plate = "" }) {
  const first = firstOfMonth(month);
  const next  = nextMonth(first);
  const qs = new URLSearchParams({ period: "custom", start: ymd(first), end: ymd(next) });
  applyFilters(qs, { bucket, light, plate });
  return `/api/traffic/daily?${qs.toString()}`;
}
export function urlMonthly({ year, bucket = "", light = "", plate = "" }) {
  const start = `${year}-01-01`, end = `${year + 1}-01-01`;
  const qs = new URLSearchParams({ period: "custom", start, end });
  applyFilters(qs, { bucket, light, plate });
  return `/api/traffic/monthly?${qs.toString()}`;
}

// ---------- fetch wrapper ----------
async function fetchJSON(url) {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}
function sumTotals(rows) {
  let s = 0;
  for (const r of rows || []) s += Number(r.Total ?? r.total ?? 0);
  return s;
}
async function getRows(url) {
  try {
    const data = await fetchJSON(url);
    const rows = Array.isArray(data?.rows) ? data.rows : [];
    const total = typeof data?.meta?.total === "number" ? data.meta.total : sumTotals(rows);
    return { rows, total };
  } catch {
    return { rows: [], total: 0 };
  }
}

/**
 * Strategy selector:
 *  - mode "none": ignore plate, just fetch
 *  - mode "partial": fetch without &exact
 *  - mode "exact": append &exact=true
 * (Matches prod semantics; no exact-first fallback.)
 */
async function loadByMode(baseUrl, mode) {
  if (mode === "exact") {
    const url = baseUrl.includes("?") ? `${baseUrl}&exact=true` : `${baseUrl}?exact=true`;
    const r = await getRows(url);
    return { rows: r.rows, used: "exact" };
  }
  // "partial" or "none"
  const r = await getRows(baseUrl);
  return { rows: r.rows, used: mode };
}

// High-level helpers the page will call:
export async function fetchToday({ day = new Date(), bucket = "", light = "", plate = "" } = {}) {
  const { value, mode } = normalizePlate(plate);
  const url = urlHourly({ day, bucket, light, plate: value });
  return loadByMode(url, mode);
}
export async function fetchMonth({ month = firstOfMonth(new Date()), bucket = "", light = "", plate = "" } = {}) {
  const { value, mode } = normalizePlate(plate);
  const url = urlDaily({ month, bucket, light, plate: value });
  return loadByMode(url, mode);
}
export async function fetchYear({ year = new Date().getFullYear(), bucket = "", light = "", plate = "" } = {}) {
  const { value, mode } = normalizePlate(plate);
  const url = urlMonthly({ year, bucket, light, plate: value });
  return loadByMode(url, mode);
}

// ------------------------------ plate search / resolve ------------------------------
/**
 * Prod-style plate resolution:
 *  1) Try EXACT first (so exact alias → canonical mapping is honored)
 *  2) Then PARTIAL (backend excludes aliases from partials)
 * Returns: array of plate strings.
 */
export async function resolvePlateCandidates(q) {
  const query = String(q || "").trim();
  if (!query) return [];

  const seen = new Set();

  // 1) EXACT
  try {
    const urlExact = `/api/search/plate?plate=${encodeURIComponent(query)}&exact=true&limit=250`;
    const dataExact = await fetchJSON(urlExact);
    for (const r of (dataExact.rows || [])) {
      const p = (r.plate || "").trim();
      if (p) seen.add(p);
    }
    if (seen.size > 0) return Array.from(seen);
  } catch (e) {
    console.warn("resolvePlateCandidates exact search failed", e);
  }

  // 2) PARTIAL (canonical plates only)
  try {
    const urlPartial = `/api/search/plate?plate=${encodeURIComponent(query)}&limit=250`;
    const dataPartial = await fetchJSON(urlPartial);
    for (const r of (dataPartial.rows || [])) {
      const p = (r.plate || "").trim();
      if (p) seen.add(p);
    }
    return Array.from(seen);
  } catch (e) {
    console.warn("resolvePlateCandidates partial search failed", e);
    return [];
  }
}

/**
 * Optional: lightweight suggestions list for typeahead UIs.
 * Uses the partial search and returns plate strings.
 */
export async function suggestPlates(q) {
  const query = String(q || "").trim();
  if (!query) return [];
  try {
    const url = `/api/search/plate?plate=${encodeURIComponent(query)}&limit=50`;
    const data = await fetchJSON(url);
    const out = [];
    for (const r of (data.rows || [])) {
      const p = (r.plate || "").trim();
      if (p) out.push(p);
    }
    return out;
  } catch (e) {
    console.warn("suggestPlates failed", e);
    return [];
  }
}
